/**
 * Blocks list page — view block records for abuse investigation.
 * Filter by blocker/blocked UUID; paginated table.
 */

import { useCallback, useEffect, useState } from "react";
import { type AdminBlock, fetchBlocks } from "../../api/admin-relations";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 20;

export function BlocksPage(): React.JSX.Element {
  const [blockerId, setBlockerId] = useState("");
  const [blockedId, setBlockedId] = useState("");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminBlock[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchBlocks({
        blocker_id: blockerId || undefined,
        blocked_id: blockedId || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [blockerId, blockedId, offset]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const resetOffset = useCallback((): void => setOffset(0), []);

  return (
    <div>
      <PageHeader
        title={t("blocks.title")}
        description={t("blocks.description")}
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <BlockFilters
        blockerId={blockerId}
        blockedId={blockedId}
        onBlockerChange={setBlockerId}
        onBlockedChange={setBlockedId}
        onSearch={resetOffset}
      />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : (
        <>
          <BlockTable items={items} />
          <BlockPagination offset={offset} total={total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}

// ─── Filters ───

type FilterProps = {
  blockerId: string;
  blockedId: string;
  onBlockerChange: (v: string) => void;
  onBlockedChange: (v: string) => void;
  onSearch: () => void;
};

function BlockFilters(p: FilterProps): React.JSX.Element {
  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") p.onSearch();
  };
  return (
    <div style={s.filterBar}>
      <input
        type="text"
        placeholder={t("blocks.blocker_placeholder")}
        value={p.blockerId}
        onChange={(e) => p.onBlockerChange(e.target.value)}
        onKeyDown={handleKey}
        style={s.searchInput}
      />
      <input
        type="text"
        placeholder={t("blocks.blocked_placeholder")}
        value={p.blockedId}
        onChange={(e) => p.onBlockedChange(e.target.value)}
        onKeyDown={handleKey}
        style={s.searchInput}
      />
      <button type="button" onClick={p.onSearch} style={s.searchBtn}>
        {t("common.search")}
      </button>
    </div>
  );
}

// ─── Table ───

function BlockTable(props: {
  items: AdminBlock[];
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("blocks.no_blocks")}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("blocks.col_blocker")}</th>
            <th style={s.th}>{t("blocks.col_blocked")}</th>
            <th style={s.th}>{t("blocks.col_created")}</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((b) => (
            <tr key={b.id}>
              <td style={s.td}>
                {b.blocker_name} ({b.blocker_email})
              </td>
              <td style={s.td}>
                {b.blocked_name} ({b.blocked_email})
              </td>
              <td style={s.td}>{formatDate(b.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Pagination ───

function BlockPagination(props: {
  offset: number;
  total: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const page = Math.floor(props.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("blocks.page_info", { total: props.total, page, totalPages })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.offset === 0}
          onClick={() => props.onChange(Math.max(0, props.offset - PAGE_SIZE))}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={props.offset + PAGE_SIZE >= props.total}
          onClick={() => props.onChange(props.offset + PAGE_SIZE)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
